import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Form, FormElement, FormHandle } from '@progress/kendo-react-form'
import { GridCellProps } from '@progress/kendo-react-grid'
import { CaseDto } from '@v1CasesApi'
import FormDateSpanSelector, { IFormDateSpanSelectorRef } from 'components/Form/FormDateSpanSelector'
import SAGrid, { GetDataResponse, GridGetDataProps, ISAGridRef } from 'components/Grid/SAGrid'
import { LS_CONTACT_HISTORY_END_DATE, LS_CONTACT_HISTORY_PAGE_SIZE, LS_CONTACT_HISTORY_RELATIVE_DATE_OPTION, LS_CONTACT_HISTORY_SEARCH, LS_CONTACT_HISTORY_SELECTED_PAGE, LS_CONTACT_HISTORY_SORT, LS_CONTACT_HISTORY_START_DATE } from 'core/consts'
import { V1ApiCasesClient } from 'core/http-common'
import toast from 'core/toast'
import moment from 'moment'
import { useRef, useState } from 'react'
import { Col, Row } from 'react-bootstrap'
import { useDispatch } from 'react-redux'
import { startLoadingAction } from 'state/actions/appActions'
import { AppDispatch } from 'state/store'
import { ICustomColumn, IDateSearchLocalStorageNames, IGridLocalStorageNames } from 'types'

interface IProps {
    editable: boolean
    caseToEdit: CaseDto | undefined
}

const ContactHistoryForm = ({ caseToEdit }: IProps) => {
    const dispatch = useDispatch<AppDispatch>()
    const gridRef = useRef<ISAGridRef>(null)
    const formDateSpanSelectorRef = useRef<IFormDateSpanSelectorRef>(null)
    const formRef = useRef<FormHandle>(null)
    const maxDate = moment().endOf('day').toDate()

    const searchByDate = async (start: Date, end: Date) => {
        formRef.current?.valueSetter('startDate', start)
        formRef.current?.valueSetter('endDate', end)
        gridRef.current?.resetAndFetch()
    }

    const dateSearchLocalStorageNames: IDateSearchLocalStorageNames = {
        relativeDateOption: LS_CONTACT_HISTORY_RELATIVE_DATE_OPTION,
        startDate: LS_CONTACT_HISTORY_START_DATE,
        endDate: LS_CONTACT_HISTORY_END_DATE
    }

    const gridLocalStorageNames: IGridLocalStorageNames =
    {
        pageSize: LS_CONTACT_HISTORY_PAGE_SIZE,
        search: LS_CONTACT_HISTORY_SEARCH,
        selectedPage: LS_CONTACT_HISTORY_SELECTED_PAGE,
        sort: LS_CONTACT_HISTORY_SORT
    }

    const getData = async (props: GridGetDataProps): Promise<GetDataResponse> => {
        try {
            if (caseToEdit && caseToEdit.caseId) {
                let values = formRef.current?.values

                const result = await V1ApiCasesClient.getCaseCallHistory(
                    caseToEdit.caseId,
                    props.search,
                    props.sortBy,
                    props.sortAscending,
                    props.paging && props.paging.currentPage ? props.paging.currentPage : 1,
                    props.paging ? props.paging.take : 10,
                    true,
                    values?.startDate ? values.startDate : undefined,
                    values?.endDate ? values.endDate : undefined,
                )

                return {
                    data: result.data,
                    total: result.totalItems ? result.totalItems : 0
                }
            }
        }
        catch (e) {
            toast.responseErrors(e)
        }
        return {
            data: [],
            total: 0
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleClaimCallHistory = async (id: number) => {
        await dispatch(startLoadingAction())
        const result = await V1ApiCasesClient.claimCallHistory(id, caseToEdit?.caseId)

        if (result.success) {
            toast.success('Contact History claimed successfully')
            gridRef.current?.fetch()
        }
        else {
            toast.responseErrors(result.errors)
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const cellCaseId = (props: GridCellProps) => {
        if (props.dataItem.caseId)
            return (<td className='text-center'></td>)

        return (
            <td className='cursor-pointer text-center' title='Click to Claim Call HIstory'
                onClick={() => handleClaimCallHistory(props.dataItem.callHistoryId)}>
                <a className='btn btn-default btn-sm gridButton' role='button' aria-disabled='true'>
                    <FontAwesomeIcon icon={['fas', 'tasks']} />
                </a>
            </td>
        )
    }

    const historyColumns: ICustomColumn[] = [
        { field: 'caseId', title: 'Claim', width: '110px', cell: cellCaseId },
        { field: 'userName', title: 'User', width: '365px' },
        { field: 'phoneNumber', title: 'Phone Number', width: '180px' },
        { field: 'startTime', title: 'Start Time', format: '{0:G}' },
        { field: 'type', title: 'Type', width: '200px' },
    ]

    return (
        <div id='ContactHistoryForm'>
            <div className='bg-light shadow-sm p-3 border pb-5'>
                <Row>
                    <Col md={6}><h4>Contact History</h4></Col>
                    <Col md={6} className='text-start text-md-end' style={{ alignSelf: 'center' }}>
                        {/* place right aligned element here if needed */}
                    </Col>
                    <Col xs={12}>
                        <Form
                            ref={formRef}
                            ignoreModified={true}
                            render={(formRenderProps) => (
                                <FormElement>
                                    <FormDateSpanSelector
                                        ref={formDateSpanSelectorRef}
                                        className=''
                                        formRenderProps={formRenderProps}
                                        localStorageNames={dateSearchLocalStorageNames}
                                        onSearch={searchByDate}
                                        maxDate={maxDate}
                                    />
                                </FormElement>
                            )}
                        />
                    </Col>
                </Row>
                <hr />
                <Row>
                    <Col xs={12}>
                        <SAGrid
                            ref={gridRef}
                            features={
                                {
                                    pageable: true,
                                    reorderColumns: true,
                                    sortable: true,
                                    simpleSearch: true
                                }
                            }
                            columns={historyColumns}
                            defaultSort={[{ field: 'startTime', dir: 'desc' }]}
                            getData={getData}
                            localStorageNames={gridLocalStorageNames}
                        />
                    </Col>
                </Row>
            </div>
        </div>
    )
}
export default ContactHistoryForm